import {FC, ReactNode, useState, useRef, useEffect} from 'react'
import {Container} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import {ArrowLeftShort} from 'react-bootstrap-icons'
import {useNavigate} from 'react-router-dom'
import styles from './UnauthenticatedLayout.module.css'

interface Language {
  code: string
  name: string
  shortCode: string
}

const languages: Language[] = [
  {code: 'fi', name: 'Suomi', shortCode: 'FI'},
  {code: 'en', name: 'English', shortCode: 'EN'},
  {code: 'sv', name: 'Svenska', shortCode: 'SV'}
]

interface UnauthenticatedLayoutProps {
  children: ReactNode
  showBackButton?: boolean
  backTo?: string
  sideImage?: string
}

const UnauthenticatedLayout: FC<UnauthenticatedLayoutProps> = ({
  children,
  showBackButton = false,
  backTo = '/login',
  sideImage
}) => {
  const {i18n} = useTranslation()
  const navigate = useNavigate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode)
    localStorage.setItem('preferredLanguage', langCode)
    setIsDropdownOpen(false)
  }

  const handleBack = () => {
    navigate(backTo)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const formContent = (
    <>
      <header className={styles.header}>
        {showBackButton && (
          <button
            className={styles.backButton}
            onClick={handleBack}
            aria-label="Go back"
          >
            <ArrowLeftShort />
          </button>
        )}

        <img
          src="/impact_logo-1x.png"
          srcSet="/impact_logo-1x.png 1x, /impact_logo-2x.png 2x"
          alt="Impact Logo"
          className={styles.logo}
        />

        <div className={styles.languageSelector} ref={dropdownRef}>
          <button
            className={styles.languageButton}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-label="Select language"
            aria-expanded={isDropdownOpen}
          >
            {currentLanguage.shortCode}
          </button>

          {isDropdownOpen && (
            <div className={styles.languageDropdown}>
              {languages.map(lang => (
                <button
                  key={lang.code}
                  className={`${styles.languageOption} ${
                    i18n.language === lang.code ? styles.languageOptionActive : ''
                  }`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className={styles.content}>
        {children}
      </main>
    </>
  )

  return (
    <Container className={styles.container} fluid>
      {sideImage ? (
        <div className={styles.wrapperWithImage}>
          <div className={styles.formPanel}>
            {formContent}
          </div>
          <div className={styles.imagePanel}>
            <img src={sideImage} alt="" className={styles.sideImage} aria-hidden="true" />
            <div className={styles.grainOverlay}>
              <svg xmlns="http://www.w3.org/2000/svg">
                <filter id="grain">
                  <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
                </filter>
                <rect width="100%" height="100%" filter="url(#grain)" />
              </svg>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.wrapper}>
          {formContent}
        </div>
      )}
    </Container>
  )
}

export default UnauthenticatedLayout
